import React from 'react'

import Image from 'next/image'
import Link from 'next/link'

import { Column, Row, Text } from 'Components/UI'

import {
  LogoEmajinGolfImage,
  LogoWorkAxleImage,
} from 'Assets/Image/CompanyLogos'
import { EmajinGolfImage, WorkAxleImage } from 'Assets/Image/Products'
import { ArrowRightGlyph, CheckGlyph } from 'Assets/Svg/Icons'

import { PROJECT } from 'Constants/Paths'

import {
  CaseCard,
  CaseContent,
  CaseImage,
  CaseLogo,
  CasesGrid,
  CheckIcon,
  CheckItem,
  CheckList,
  LearnMoreLink,
  SectionWrapper,
  StatItem,
  StatLabel,
  StatsGrid,
  StatValue,
  Strong,
} from './styles'

const CASES = [
  {
    id: 'emajin',
    label: 'Case A',
    labelColor: '#2781ab',
    backgroundColor: '#eaf1f5',
    logo: LogoEmajinGolfImage,
    name: 'Emajin.Golf',
    description:
      'We built a premium membership app with bookings, newsfeeds, messaging, and live RSVPs - helping Emajin scale its exclusive golf network across Australia and beyond.',
    benefits: ['72% faster time-to-market', '58% development cost reduction'],
    image: EmajinGolfImage,
    slug: 'emajingolf-app',
  },
  {
    id: 'workaxle',
    label: 'Case B',
    labelColor: '#f48961',
    backgroundColor: '#f9f2f0',
    logo: LogoWorkAxleImage,
    name: 'WorkAxle',
    description:
      'We delivered a real-time scheduling engine with shift tracking, employee availability, and location-aware clock-ins - making workforce ops effortless for teams across North America.',
    benefits: ['30,000+ users online', 'Enterprise in under 3 years'],
    image: WorkAxleImage,
    slug: 'workaxle',
  },
] as const

// CaseImage caps its img at 400px, but the two-column grid squeezes it well
// below that on wide screens. Measured: 1290px+ -> 260 CSS px, 1024 -> 232,
// 768 -> 400 (cap), 412 -> 380.
const CASE_IMAGE_SIZES =
  '(min-width: 992px) 260px, (min-width: 400px) 400px, 100vw'

const STATS = [
  { value: '120+', label: 'products shipped since 2015' },
  { value: '20,000+', label: 'engineering hours in our internal frameworks' },
  {
    value: 'Senior only',
    label: 'the engineers who scope your build write it',
  },
  { value: 'Paid discovery', label: 'opens every engagement' },
  { value: 'Sydney', label: 'HQ, with engineers across EU and APAC hours' },
  { value: '11-50', label: 'a small senior team, by choice' },
] as const

function RealProductsRealResultsSection() {
  return (
    <SectionWrapper>
      <Text as="h2" balanceWrap center mb="80px" semiLight title title40>
        Real products. <Strong>Real results.</Strong>
      </Text>

      <CasesGrid>
        {CASES.map(
          ({
            id,
            label,
            labelColor,
            backgroundColor,
            logo,
            name,
            description,
            benefits,
            image,
            slug,
          }) => (
            <CaseCard background={backgroundColor} key={id}>
              <CaseContent>
                <Row alignCenter gap={5}>
                  <CaseLogo>
                    <Image alt={name} src={logo} />
                  </CaseLogo>

                  <Column spaceBetween>
                    <Text body12 medium style={{ color: labelColor }}>
                      {label}
                    </Text>
                    <Text body16 medium title>
                      {name}
                    </Text>
                  </Column>
                </Row>

                <Text body body14 semiLight>
                  {description}
                </Text>

                <CheckList>
                  {benefits.map(benefit => (
                    <CheckItem key={benefit}>
                      <CheckIcon>{CheckGlyph()}</CheckIcon>
                      <Text body body14 medium>
                        {benefit}
                      </Text>
                    </CheckItem>
                  ))}
                </CheckList>

                <LearnMoreLink
                  aria-label={`Learn more about ${name}`}
                  as={Link}
                  href={PROJECT(slug)}
                >
                  <span>Learn more</span>
                  <ArrowRightGlyph height={20} width={20} />
                </LearnMoreLink>
              </CaseContent>

              <CaseImage>
                <Image
                  alt={`${name} screenshot`}
                  sizes={CASE_IMAGE_SIZES}
                  src={image}
                />
              </CaseImage>
            </CaseCard>
          ),
        )}
      </CasesGrid>

      <StatsGrid>
        {STATS.map(({ value, label }) => (
          <StatItem key={value}>
            <StatValue>{value}</StatValue>
            <StatLabel>{label}</StatLabel>
          </StatItem>
        ))}
      </StatsGrid>
    </SectionWrapper>
  )
}

export default RealProductsRealResultsSection
